import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import {
  SeatmapResultState,
  resetSeatmapResult,
  selectSeatmapResultIsFailureStatus,
  selectSeatmapResultState,
  setSeatmapResultFromApi,
} from '../../store';
import { ErrorType, PageType } from '@lib/interfaces';
import { ApiErrorResponseService, ErrorsHandlerService } from '@lib/services';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import { DefaultService as Api, PostSeatmapRequest, PostSeatmapResponse } from 'src/sdk_checkin';

@Injectable({
  providedIn: 'root',
})
export class SeatmapResultStoreService extends CheckinBaseClass {
  private _result$: Observable<SeatmapResultState>;
  private _result!: SeatmapResultState;
  get result() {
    return this._result;
  }

  /** APIから返却されたエラーコードをエラーメッセージIDに変換 */
  private readonly API_ERROR_CODE_TO_MESSAGE_KEY: { [errorCode: string]: string } = {
    EBAZ000357: 'E0624',
  };

  /** APIエラーSubscription */
  apiErrorResponseSubscription?: Subscription;

  constructor(
    private _store: Store<SeatmapResultState>,
    private _api: Api,
    private _apiErrorResponseService: ApiErrorResponseService,
    private _errorsHandlerService: ErrorsHandlerService
  ) {
    super();
    this._result$ = this._store.pipe(
      select(selectSeatmapResultState),
      filter((data) => !!data)
    );
    this.subscribeService(this._result$, (data) => {
      this._result = data;
    });
  }

  override destroy(): void {
    if (!!this.apiErrorResponseSubscription) {
      this.apiErrorResponseSubscription.unsubscribe();
      this.apiErrorResponseSubscription = undefined;
    }
  }

  /**
   * SeatmapResultState のObservableを返却する
   * @returns SeatmapResultState の全ての値を取得するObservable
   */
  public getResultState$() {
    return this._result$;
  }

  /**
   * PostSeatmapResponseData を取得するObservableを返却する
   * @returns
   */
  public getResponse$() {
    return this._result$.pipe(filter((data) => !!data.model?.data));
  }

  /**
   * API呼び出しが失敗したかのObservableを返却する
   * @returns isFailureの値を取得するObservable
   */
  getIsFailure$() {
    return this._store.pipe(select(selectSeatmapResultIsFailureStatus));
  }

  /**
   * シートマップ取得が正常終了したら結果を取得するObservable
   */
  getResultStateIfSuccessful$() {
    return this._result$.pipe(filter((state) => !state.isFailure && !state.isPending && !!state.model));
  }

  /**
   * API呼び出し結果のObservableを受け取りStoreに格納する
   * @param call API呼び出し結果を取得するObservable
   */
  private setResult(call: Observable<PostSeatmapResponse>) {
    this._store.dispatch(setSeatmapResultFromApi({ call }));
  }

  /**
   * シートマップ取得のストアをクリアする
   */
  public clear() {
    this._store.dispatch(resetSeatmapResult());
  }

  /**
   * シートマップ取得APIの呼び出しと、呼び出し結果のStoreへの設定を行う
   *
   * エラーハンドリングの設定は、呼び出しもとで実施する。
   */
  callApi(condition: PostSeatmapRequest) {
    const call = this._api.checkinGetSeatmapPost(condition);
    this.setResult(call);
  }

  /**
   * シートマップ取得APIのエラーを監視して処理する。
   */
  setupApiErrorHandler() {
    if (!!this.apiErrorResponseSubscription) {
      return;
    }
    this.apiErrorResponseSubscription = this.getIsFailure$()
      .pipe(
        withLatestFrom(this._apiErrorResponseService.getApiErrorResponse$(), (_, state) => state),
        map((state) => {
          const errorLength = state?.errors?.length;
          if (!!errorLength && errorLength > 0) {
            const code = state?.errors?.[0]?.code;
            const matchErrorMsgId = code && this.getErrorMessageKey(code);
            if (matchErrorMsgId) {
              this._errorsHandlerService.setRetryableError(PageType.PAGE, {
                errorMsgId: matchErrorMsgId,
                apiErrorCode: code,
              });
            } else {
              this._errorsHandlerService.setNotRetryableError({ errorType: ErrorType.SYSTEM });
            }
          }
        })
      )
      .subscribe();
  }

  /**
   * エラーコードを受け取り、エラーメッセージIDを返却する
   * @param apiErrorCode
   * @returns 指定されたエラーコードに紐づくエラーメッセージID、存在しない場合はundefined
   */
  getErrorMessageKey(apiErrorCode: string): string | undefined {
    return this.API_ERROR_CODE_TO_MESSAGE_KEY[apiErrorCode];
  }
}
